import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import useAuthStore from '../store/authStore';
import { getTutorialConfig, TUTORIAL_VERSION } from '../config/tutorialSteps';
import './TutorialOverlay.css';

const TutorialOverlay = () => {
  const { t, i18n } = useTranslation();
  const {
    showTutorial,
    tutorialSteps,
    setShowTutorial,
    completeTutorial,
    skipTutorial
  } = useAuthStore();

  const [stepIndex, setStepIndex] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [placement, setPlacement] = useState('bottom');
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const tooltipRef = useRef(null);

  // Check if current language is RTL
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';

  // Get tutorial configuration with RTL support
  const tutorialConfig = getTutorialConfig(isRTL);

  // Current step
  const currentStep = tutorialSteps[stepIndex];

  // Calculate tooltip position based on target element
  const calculatePosition = useCallback((target, stepPlacement) => {
    if (!target || !tooltipRef.current) return;

    const targetRect = target.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;

    let top = 0;
    let left = 0;
    let finalPlacement = stepPlacement || 'bottom';

    // Calculate position based on placement
    switch (finalPlacement) {
      case 'top':
        top = targetRect.top + scrollY - tooltipRect.height - 20;
        left = targetRect.left + scrollX + (targetRect.width / 2) - (tooltipRect.width / 2);
        break;
      case 'bottom':
        top = targetRect.bottom + scrollY + 20;
        left = targetRect.left + scrollX + (targetRect.width / 2) - (tooltipRect.width / 2);
        break;
      case 'left':
        top = targetRect.top + scrollY + (targetRect.height / 2) - (tooltipRect.height / 2);
        left = targetRect.left + scrollX - tooltipRect.width - 20;
        break;
      case 'right':
        top = targetRect.top + scrollY + (targetRect.height / 2) - (tooltipRect.height / 2);
        left = targetRect.right + scrollX + 20;
        break;
      case 'center':
        top = (window.innerHeight / 2) - (tooltipRect.height / 2) + scrollY;
        left = (window.innerWidth / 2) - (tooltipRect.width / 2) + scrollX;
        break;
      default:
        top = targetRect.bottom + scrollY + 20;
        left = targetRect.left + scrollX + (targetRect.width / 2) - (tooltipRect.width / 2);
    }

    // Keep tooltip within viewport bounds
    const padding = 10;
    if (left < padding) left = padding;
    if (left + tooltipRect.width > window.innerWidth - padding) {
      left = window.innerWidth - tooltipRect.width - padding;
    }
    if (top < padding + scrollY) top = padding + scrollY;

    setTooltipPosition({ top, left });
    setPlacement(finalPlacement);
  }, []);

  // Update position when step changes
  useEffect(() => {
    if (!showTutorial || !currentStep) return;

    const updatePosition = () => {
      const target = currentStep.target === 'body'
        ? document.body
        : document.querySelector(currentStep.target);

      if (target && tooltipRef.current) {
        // Scroll target into view
        if (currentStep.target !== 'body') {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        // Calculate position after a short delay to ensure scroll is complete
        setTimeout(() => {
          calculatePosition(target, currentStep.placement);
        }, 300);
      } else if (stepIndex < tutorialSteps.length - 1) {
        // Target not found, skip to next step
        console.warn(`Tutorial target not found for step ${stepIndex}: ${currentStep.target}`);
        setStepIndex(stepIndex + 1);
      } else {
        // Last step and target not found, finish tutorial
        handleComplete();
      }
    };

    updatePosition();

    // Recalculate on window resize or scroll
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [stepIndex, showTutorial, currentStep, calculatePosition, tutorialSteps.length]);

  // Restore step index on mount if tutorial was in progress
  useEffect(() => {
    const savedStep = sessionStorage.getItem('tutorialCurrentStep');
    if (savedStep && showTutorial) {
      setStepIndex(parseInt(savedStep, 10));
    }
  }, [showTutorial]);

  // Store current step in sessionStorage
  useEffect(() => {
    if (showTutorial) {
      sessionStorage.setItem('tutorialCurrentStep', stepIndex.toString());
    }
  }, [stepIndex, showTutorial]);

  const handleNext = () => {
    if (stepIndex < tutorialSteps.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
    }
  };

  const handleComplete = () => {
    setStepIndex(0);
    sessionStorage.removeItem('tutorialCurrentStep');
    completeTutorial(TUTORIAL_VERSION);
  };

  const handleSkip = () => {
    setShowSkipConfirm(true);
  };

  const handleSkipConfirm = (permanently) => {
    setStepIndex(0);
    sessionStorage.removeItem('tutorialCurrentStep');
    setShowSkipConfirm(false);
    skipTutorial(permanently);
  };

  // Don't render if tutorial is not active
  if (!showTutorial || !currentStep) {
    return null;
  }

  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === tutorialSteps.length - 1;

  // Get translated button labels
  const skipLabel = currentStep.locale?.skip
    ? t(currentStep.locale.skip)
    : t('tutorial.navigation.skip');
  const backLabel = currentStep.locale?.back
    ? t(currentStep.locale.back)
    : t('tutorial.navigation.back');
  const nextLabel = isLastStep
    ? (currentStep.locale?.last ? t(currentStep.locale.last) : t('tutorial.navigation.last'))
    : (currentStep.locale?.next ? t(currentStep.locale.next) : t('tutorial.navigation.next'));

  // Get target element for spotlight
  const targetElement = currentStep.target === 'body'
    ? null
    : document.querySelector(currentStep.target);

  const targetRect = targetElement?.getBoundingClientRect();

  return createPortal(
    <>
      {/* Overlay */}
      <div
        className="tutorial-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9998,
          pointerEvents: 'none'
        }}
      />

      {/* Spotlight for target element */}
      {targetElement && targetRect && (
        <div
          className="tutorial-spotlight"
          style={{
            position: 'absolute',
            top: targetRect.top + window.scrollY - 8,
            left: targetRect.left + window.scrollX - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
            border: '2px solid #10b981',
            borderRadius: '8px',
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            pointerEvents: 'none',
            transition: 'all 0.3s ease'
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={`tutorial-tooltip tutorial-tooltip-${placement}`}
        style={{
          position: 'absolute',
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          backgroundColor: tutorialConfig.styles.options.backgroundColor,
          borderRadius: tutorialConfig.styles.tooltip.borderRadius,
          padding: tutorialConfig.styles.tooltip.padding,
          fontSize: tutorialConfig.styles.tooltip.fontSize,
          color: tutorialConfig.styles.options.textColor,
          width: tutorialConfig.styles.options.width,
          zIndex: 10000,
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
          textAlign: isRTL ? 'right' : 'left',
          direction: isRTL ? 'rtl' : 'ltr',
          transition: 'all 0.3s ease'
        }}
      >
        {/* Progress indicator */}
        {tutorialConfig.showProgress && (
          <div style={{
            fontSize: '12px',
            color: 'var(--color-text-tertiary, #9ca3af)',
            marginBottom: '12px',
            fontWeight: '600'
          }}>
            {stepIndex + 1} / {tutorialSteps.length}
          </div>
        )}

        {/* Content */}
        <div style={{
          marginBottom: '20px',
          lineHeight: '1.6'
        }}>
          {t(currentStep.content)}
        </div>

        {/* Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px'
        }}>
          {/* Skip button */}
          {tutorialConfig.showSkipButton && (
            <button
              onClick={handleSkip}
              style={{
                background: 'none',
                border: 'none',
                color: tutorialConfig.styles.buttonSkip.color,
                fontSize: tutorialConfig.styles.buttonSkip.fontSize,
                cursor: 'pointer',
                padding: '8px 12px'
              }}
            >
              {skipLabel}
            </button>
          )}

          <div style={{ flex: 1 }} />

          {/* Navigation buttons */}
          <div style={{ display: 'flex', gap: '8px' }}>
            {!isFirstStep && (
              <button
                onClick={handleBack}
                style={{
                  background: 'none',
                  border: 'none',
                  color: tutorialConfig.styles.buttonBack.color,
                  fontSize: tutorialConfig.styles.buttonBack.fontSize,
                  cursor: 'pointer',
                  padding: '10px 16px',
                  marginRight: tutorialConfig.styles.buttonBack.marginRight
                }}
              >
                {backLabel}
              </button>
            )}

            <button
              onClick={handleNext}
              style={{
                backgroundColor: tutorialConfig.styles.buttonNext.backgroundColor,
                color: '#fff',
                border: 'none',
                borderRadius: tutorialConfig.styles.buttonNext.borderRadius,
                padding: tutorialConfig.styles.buttonNext.padding,
                fontSize: tutorialConfig.styles.buttonNext.fontSize,
                fontWeight: tutorialConfig.styles.buttonNext.fontWeight,
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              {nextLabel}
            </button>
          </div>
        </div>
      </div>

      {/* Skip Confirmation Modal — replaces window.confirm */}
      {showSkipConfirm && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          zIndex: 10001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface, #fff)',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '420px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            textAlign: isRTL ? 'right' : 'left',
            direction: isRTL ? 'rtl' : 'ltr',
          }}>
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '1.125rem', fontWeight: 700, color: 'var(--color-text-primary, #111827)' }}>
              {t('tutorial.skip.title', 'Skip Tutorial')}
            </h3>
            <p style={{ margin: '0 0 1.5rem', color: 'var(--color-text-secondary, #6b7280)', lineHeight: 1.6, fontSize: '0.95rem' }}>
              {t('tutorial.skip.message')}
              {'\n'}
              {t('tutorial.skip.question')}
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: isRTL ? 'flex-start' : 'flex-end' }}>
              <button
                onClick={() => setShowSkipConfirm(false)}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid var(--color-border-dark, #d1d5db)',
                  borderRadius: '8px',
                  backgroundColor: 'var(--color-surface, #fff)',
                  color: 'var(--color-text-secondary, #374151)',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={() => handleSkipConfirm(false)}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid var(--color-border-dark, #d1d5db)',
                  borderRadius: '8px',
                  backgroundColor: 'var(--color-surface-alt, #f3f4f6)',
                  color: 'var(--color-text-secondary, #374151)',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                {t('tutorial.skip.skipOnce', 'Skip for Now')}
              </button>
              <button
                onClick={() => handleSkipConfirm(true)}
                style={{
                  padding: '0.5rem 1rem',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#10b981',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {t('tutorial.skip.skipPermanently', 'Skip Permanently')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default TutorialOverlay;
